import _ from 'lodash';
import { useMemo } from 'react';
import { MARKETPLACE_TIERS } from '@onward-delivery/core';

const EDITABLE_ATTR = ['add_margin_flat', 'add_margin_rate', 'zip_geofence', 'tariff_id'];

const carrierOverrideMatchesPricingOverride = ({ override, pricingOverride }) => {
    const sameCarrier = override.client_id === pricingOverride?.assign_id;
    const sameAlgo = override.algo_type === pricingOverride?.algo_type;
    const sameOverrideType = override.override_type === pricingOverride?.override_type;
    const samePricingParameters = _.isEqual(
        override.pricing_parameters || {},
        pricingOverride?.pricing_parameters || {}
    );

    return sameCarrier && sameAlgo && sameOverrideType && samePricingParameters;
};

export const useMergedOptions = ({ shipper, tariffResults, activeResults, optionResults, overrides, canSeeOption }) => {
    const [adminTariffs, tariffsByClient] = useMemo(() => {
        return (tariffResults?.results || []).reduce(
            ([adminAcc, byClientAcc], tariff) => {
                let sanitizedAlgo = tariff.algo_type;
                if (['DEFAULT', 'SHIPPER_GLOBAL_DEFAULT', 'CARRIER_GLOBAL_DEFAULT'].includes(tariff.algo_type)) {
                    sanitizedAlgo = 'DEFAULT';
                }

                if (tariff.client_id) {
                    byClientAcc[sanitizedAlgo] = [
                        ...(byClientAcc[sanitizedAlgo] || []),
                        { label: tariff.name, value: tariff.tariff_id, client_id: tariff.client_id },
                    ];
                } else {
                    adminAcc[sanitizedAlgo] = [
                        ...(adminAcc[sanitizedAlgo] || []),
                        { label: tariff.name, value: tariff.tariff_id },
                    ];
                }

                return [adminAcc, byClientAcc];
            },
            [{}, {}]
        );
    }, [tariffResults]);

    const [activeIntegration, activePricing, activeCarrierPricing] = useMemo(() => {
        return [activeResults?.integration || [], activeResults?.pricing || [], activeResults?.carrier_pricing || []];
    }, [activeResults]);

    const options = useMemo(() => {
        return optionResults?.results.filter((option) => option.integration_overrides.length > 0) || [];
    }, [optionResults]);

    const marketplaceOption = useMemo(() => {
        const mktplace = activePricing.find((option) => !option.assign_id);

        return {
            ...(mktplace ? Object.fromEntries(EDITABLE_ATTR.map((attr) => [attr, mktplace[attr]])) : {}),
            ...overrides.Marketplace,
            name: 'Marketplace',
            id: 'Marketplace',
            admin_tariff_options: [...(adminTariffs.ONWARD_CONSOLIDATED || []), ...(adminTariffs.DEFAULT || [])],
            client_tariff_options: [],
            read_only: false,
            marketplace_tier: MARKETPLACE_TIERS.T3_FULLY_MANAGED,
            algo_type: 'DEFAULT + ONWARD_CONSOLIDATED',
        };
    }, [activePricing, adminTariffs, overrides]);

    const pricingOptions = useMemo(() => {
        return {
            TIER_1: options
                .filter(
                    (option) =>
                        option.marketplace_tier === MARKETPLACE_TIERS.T1_INTEGRATIONS_ONLY &&
                        (option.client_id === null || option.client_id === shipper)
                )
                .map((option) => ({
                    label: option.name,
                    value: option.integration_type_id,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
            TIER_2: options
                .filter(
                    (option) =>
                        [MARKETPLACE_TIERS.T2_INTEGRATIONS_BILLING, MARKETPLACE_TIERS.T2_INTEGRATIONS_ONLY].includes(
                            option.marketplace_tier
                        ) &&
                        (option.client_id === null || option.client_id === shipper)
                )
                .map((option) => ({
                    label: option.name,
                    value: option.integration_type_id,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        };
    }, [options, shipper]);

    const optionsMap = useMemo(() => {
        return Object.fromEntries(
            options.map((option) => {
                const pricingOverride = option?.pricing_overrides?.[0];
                const client_options = (tariffsByClient?.[pricingOverride?.algo_type] || []).filter((tariff) =>
                    canSeeOption({ option, tariff })
                );
                const matchingCarrierOverrides = (option?.carrier_pricing_overrides || []).filter((override) =>
                    carrierOverrideMatchesPricingOverride({ override, pricingOverride })
                );
                const carrierTariffIds = _.uniq(
                    matchingCarrierOverrides.map((override) => override.tariff_id).filter(Boolean)
                );
                const hasUniqueCarrierTariff = carrierTariffIds.length === 1;
                const hasNoCarrierTariffCandidates = carrierTariffIds.length === 0;
                let defaultCarrierTariffId = null;
                if (hasUniqueCarrierTariff) {
                    defaultCarrierTariffId = carrierTariffIds[0];
                } else if (hasNoCarrierTariffCandidates) {
                    defaultCarrierTariffId = pricingOverride?.tariff_id;
                }

                return [
                    option.integration_type_id,
                    {
                        ...Object.fromEntries(EDITABLE_ATTR.map((attr) => [attr, pricingOverride?.[attr]])),
                        carrier_tariff_id: defaultCarrierTariffId,
                        requires_carrier_tariff:
                            option?.marketplace_tier === MARKETPLACE_TIERS.T2_INTEGRATIONS_BILLING &&
                            matchingCarrierOverrides.length > 0,
                        name: option.name,
                        id: option?.integration_type_id,
                        marketplace_tier: option?.marketplace_tier,
                        admin_tariff_options: adminTariffs[pricingOverride?.algo_type] || [],
                        client_tariff_options: client_options,
                        algo_type: pricingOverride?.algo_type || 'DEFAULT',
                    },
                ];
            })
        );
    }, [options, adminTariffs, tariffsByClient, canSeeOption]);

    const [activeIntegrationMap, activePricingMap] = useMemo(() => {
        return [
            activeIntegration.reduce((acc, override) => {
                acc[override.integration_meta_type_id] = [...(acc[override.integration_meta_type_id] || []), override];
                return acc;
            }, {}),
            activePricing.reduce((acc, override) => {
                const path = [override.assign_id, override.algo_type, override.override_type];
                _.set(acc, path.join('.'), [...(_.get(acc, path.join('.'), []) || []), override]);

                return acc;
            }, {}),
        ];
    }, [activeIntegration, activePricing]);

    const merged = useMemo(() => {
        return options.reduce((acc, option) => {
            if (activeIntegrationMap?.[option?.integration_type_id]?.length > 0) {
                const optionDefaults = optionsMap[option.integration_type_id] || {};
                const pricingOverride = option?.pricing_overrides?.[0];
                const activeCarrierTariffId = activeCarrierPricing.find((override) => {
                    const sameShipper = override.partner_client_id === shipper;
                    const matchesPricing = carrierOverrideMatchesPricingOverride({ override, pricingOverride });

                    return sameShipper && matchesPricing;
                })?.tariff_id;

                acc[option.integration_type_id] = {
                    ...Object.fromEntries(
                        EDITABLE_ATTR.map((attr) => {
                            const path = [
                                pricingOverride?.assign_id,
                                pricingOverride?.algo_type,
                                pricingOverride?.override_type,
                            ];
                            const active = _.get(activePricingMap, path.join('.'), []);

                            return [attr, active[0]?.[attr]];
                        })
                    ),
                    carrier_tariff_id: activeCarrierTariffId || optionDefaults.carrier_tariff_id,
                    requires_carrier_tariff: optionDefaults.requires_carrier_tariff,
                    read_only: false,
                    name: option.name,
                    id: option?.integration_type_id,
                    marketplace_tier: option?.marketplace_tier,
                    admin_tariff_options: optionDefaults.admin_tariff_options || [],
                    client_tariff_options: optionDefaults.client_tariff_options || [],
                };
            }

            return acc;
        }, {});
    }, [options, activeIntegrationMap, activePricingMap, activeCarrierPricing, optionsMap, shipper]);

    return {
        merged,
        marketplaceOption,
        pricingOptions,
        optionsMap,
    };
};
